import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Avatar,
  Badge,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Divider,
  Rating,   
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Pending,
  LocalOffer,
  Chat,
  Send,
  Info,
  Star,
} from '@mui/icons-material';
import axios from 'axios';
import { io } from 'socket.io-client';

const socket = io(process.env.REACT_APP_API_URL);

// --- Restriction helpers ---
const wordsToNumbers = (text) => {
  const map = {
    zero: '0', one: '1', two: '2', three: '3', four: '4',
    five: '5', six: '6', seven: '7', eight: '8', nine: '9',
  };
  return text.replace(/\b(zero|one|two|three|four|five|six|seven|eight|nine)\b/gi, (m) => map[m.toLowerCase()]);
};

const detectPhoneNumbers = (text) => {
  const converted = wordsToNumbers(text);
  return /(\+?\d{1,3})?[-.\s()]?\d{3,15}/g.test(converted);
};

const detectEmails = (text) => {
  const cleaned = text
    .replace(/\s+/g, '')
    .replace(/\[at\]|\(at\)/gi, '@')
    .replace(/\s*@\s*/g, '@')
    .replace(/\s*\.\s*/g, '.');
  return /[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+/.test(cleaned);
};

const SPAM_WORDS = ['buy now', 'offer', 'deal', 'cheap', 'click here', 'whatsapp', 'contact', 'email me', 'promotion'];

const UserDashboard = ({ user }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [openChat, setOpenChat] = useState(false);
  const [activeEnquiry, setActiveEnquiry] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });
  const [openDetails, setOpenDetails] = useState(false);
    const [openReview, setOpenReview] = useState(false); // ⭐️ Added
  const [reviewRating, setReviewRating] = useState(0); // ⭐️ Added
  const [reviewText, setReviewText] = useState('');    // ⭐️ Added
  const messagesEndRef = useRef(null);

  const hasRestrictedContent = useCallback((text) => {
    if (detectEmails(text)) return '⚠️ Email sharing restricted';
    if (detectPhoneNumbers(text)) return '⚠️ Phone number sharing restricted';
    if (SPAM_WORDS.some((w) => text.toLowerCase().includes(w))) return '⚠️ Restricted term detected';
    return null;
  }, []);

  const formatTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Fetch user enquiries
  useEffect(() => {
    const fetchUserEnquiries = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/inquiries/user-by-email/${encodeURIComponent(user.email)}`
        );
        setEnquiries(Array.isArray(res.data) ? res.data : res.data.enquiries || []);
      } catch (err) {
        console.error('Error fetching enquiries:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.email) fetchUserEnquiries();
  }, [user]);

  // Open chat
  const handleOpenChat = async (enquiry) => {
    setActiveEnquiry(enquiry);
    setOpenChat(true);
    socket.emit('joinInquiry', enquiry._id);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/inquiries/${enquiry._id}/messages`);
      setMessages(res.data || []);
    } catch (err) {
      console.error('Chat history error:', err);
    }
  };

  // Open details
  const handleOpenDetails = (enquiry) => {
    setActiveEnquiry(enquiry);
    setOpenDetails(true);
  };

  // ⭐️ Handle Review Dialog Open
  const handleOpenReview = (enquiry) => {
    setActiveEnquiry(enquiry);
    setReviewRating(enquiry.rating || 0);
    setReviewText(enquiry.review || '');
    setOpenReview(true);
  };

  // ⭐️ Submit Review
  const handleSubmitReview = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/inquiries/${activeEnquiry._id}/review`,
        {
          rating: reviewRating,
          review: reviewText,
          userId: user._id,
        }
      );

      // Update enquiry list locally
      setEnquiries((prev) =>
        prev.map((e) =>
          e._id === activeEnquiry._id ? { ...e, rating: reviewRating, review: reviewText } : e
        )
      );

      setSnackbar({ open: true, message: 'Review submitted successfully!', severity: 'success' });
      setOpenReview(false);
    } catch (err) {
      console.error('Review submission failed:', err);
      setSnackbar({ open: true, message: 'Failed to submit review.', severity: 'error' });
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!chatInput.trim()) return;

    const restriction = hasRestrictedContent(chatInput);
    if (restriction) {
      setSnackbar({ open: true, message: restriction, severity: 'warning' });
      return;
    }

    const msg = {
      senderId: user._id || user.id,
      senderType: 'customer',
      content: chatInput,
      timestamp: new Date(),
    };

    setChatInput('');
    socket.emit('sendMessage', { ...msg, inquiryId: activeEnquiry._id });
    await axios.post(`${process.env.REACT_APP_API_URL}/api/inquiries/${activeEnquiry._id}/messages`, msg);
  };

  // Real-time message updates
  useEffect(() => {
    socket.on('newMessage', (msg) => {
      if (msg.inquiryId === activeEnquiry?._id) {
        setMessages((prev) => [...prev, msg]);
      }
    });
    return () => socket.off('newMessage');
  }, [activeEnquiry]);

  const filtered = enquiries.filter((e) => {
    const s = e.status?.toLowerCase();
    const match = e.service?.toLowerCase().includes(searchTerm.toLowerCase());
    if (activeTab === 1) return match && s === 'assigned';
    if (activeTab === 2) return match && s === 'completed';
    return match;
  });

  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* 🌟 Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4" fontWeight={700}>User Dashboard</Typography>
        <Badge badgeContent={enquiries.length} color="primary">
          <Avatar sx={{ bgcolor: '#03BAA5' }}>{user?.name?.[0]}</Avatar>
        </Badge>
      </Box>

      {/* 🧭 Tabs & Search */}
      <Paper
        sx={{
          p: 2,
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(90deg, #03BAA5 0%, #4B698E 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: 'white', height: 3 } }}
            sx={{
              '& .MuiTab-root': { color: '#fff', fontWeight: 600, textTransform: 'none' },
              '& .Mui-selected': { color: '#ffeb3b !important' },
            }}
          >
            <Tab label="All Enquiries" />
            <Tab label="Active" icon={<Pending />} iconPosition="start" />
            <Tab label="Completed" icon={<CheckCircle />} iconPosition="start" />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search enquiries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              width: { xs: '100%', sm: 280 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* 📋 Enquiries Table */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 4 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#03BAA5' }}>
              <TableRow>
                {['Service', 'Dealer', 'Date', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 600, textTransform: 'uppercase' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((e) => (
                <TableRow hover key={e._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocalOffer color="primary" /> {e.service}
                    </Box>
                  </TableCell>
                  <TableCell>{e.assignedDealer?.name || 'Unassigned'}</TableCell>
                  <TableCell>{new Date(e.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={e.status}
                      color={e.status === 'completed' ? 'success' : 'info'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
  <Button
    size="small"
    variant="outlined"
    startIcon={<Info />}
    color="secondary"
    onClick={() => handleOpenDetails(e)}
    sx={{ mr: 1 }}
  >
    View Details
  </Button>

  <Button
    size="small"
    variant="contained"
    startIcon={<Chat />}
    onClick={() => handleOpenChat(e)}
    sx={{
      mr: 1,
      background: 'linear-gradient(90deg, #03BAA5, #4B698E)',
      '&:hover': { background: 'linear-gradient(90deg, #4B698E, #03BAA5)' },
    }}
  >
    Chat
  </Button>

  {/* ⭐️ Show Review Button only if completed */}
  {e.status === 'completed' && (
    <Button
      size="small"
      variant="contained"
      startIcon={<Star />}
      color={e.rating ? 'success' : 'warning'}
      onClick={() => handleOpenReview(e)}
    >
      {e.rating ? 'View Review' : 'Rate Dealer'}
    </Button>
  )}
</TableCell>

                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
       {/* ⭐️ Review Dialog */}
      <Dialog open={openReview} onClose={() => setOpenReview(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#03BAA5', color: 'white' }}>Rate & Review Dealer</DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {activeEnquiry?.assignedDealer?.name || 'Dealer'}
          </Typography>
          <Rating
            value={reviewRating}
            onChange={(e, val) => setReviewRating(val)}
            precision={0.5}
            size="large"
          />
          <TextField
            multiline
            rows={3}
            fullWidth
            sx={{ mt: 2 }}
            label="Write your feedback"
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReview(false)}>Cancel</Button>
          <Button onClick={handleSubmitReview} variant="contained" disabled={!reviewRating}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🧾 View Details Dialog */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            background: 'linear-gradient(90deg, #03BAA5, #4B698E)',
            color: 'white',
            fontWeight: 600,
          }}
        >
          Enquiry Details
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {activeEnquiry && (
            <>
              <Typography variant="h6" gutterBottom>{activeEnquiry.service}</Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {activeEnquiry.description || 'No description provided.'}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography><strong>Status:</strong> {activeEnquiry.status}</Typography>
              <Typography><strong>Dealer:</strong> {activeEnquiry.assignedDealer?.name || 'Unassigned'}</Typography>
              <Typography><strong>Date:</strong> {new Date(activeEnquiry.createdAt).toLocaleDateString()}</Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* 💬 Chat Dialog (Separate) */}
      <Dialog open={openChat} onClose={() => setOpenChat(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            background: 'linear-gradient(90deg, #03BAA5, #4B698E)',
            color: 'white',
            fontWeight: 600,
          }}
        >
          Chat with Dealer
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
            ⚠️ No contact info allowed
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: '#f8f9fb', height: 400, overflowY: 'auto' }}>
          {messages.map((m, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: m.senderType === 'customer' ? 'flex-end' : 'flex-start', my: 1 }}>
              <Box
                sx={{
                  bgcolor: m.senderType === 'customer' ? '#03BAA5' : '#E0E0E0',
                  color: m.senderType === 'customer' ? 'white' : 'black',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  maxWidth: '70%',
                }}
              >
                <Typography variant="body2">{m.content}</Typography>
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', opacity: 0.7 }}>
                  {formatTime(m.timestamp)}
                </Typography>
              </Box>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1 }}>
          <TextField
            fullWidth
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder="Type a message..."
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            size="small"
          />
          <Button onClick={sendMessage} variant="contained" endIcon={<Send />}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDashboard;
